import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useConfig } from "../lib/config";
import { toast } from "sonner";
import api from "../lib/api";

export default function Register() {
  const { register, user } = useAuth();
  const { config } = useConfig();
  const nav = useNavigate();
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    password: "",
    role: "facultad",
    facultad_id: "",
    unidad_id: "",
  });
  const [facultades, setFacultades] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    api.get("/facultades").then((r) => setFacultades(r.data)).catch(() => {});
    api.get("/unidades").then((r) => setUnidades(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.role !== "facultad") payload.facultad_id = null;
      if (payload.role !== "unidad") payload.unidad_id = null;
      await register(payload);
      toast.success("Cuenta creada");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear cuenta");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const availableUnidades = form.facultad_id
    ? unidades.filter((u) => u.facultad_id === form.facultad_id)
    : unidades;

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <div className="w-full max-w-md border border-zinc-200 p-10 bg-white">
        <div className="flex items-baseline gap-1 mb-6">
          <span className="text-2xl font-semibold tracking-tighter">vincularedu</span>
          <span className="text-2xl font-medium text-zinc-400">.</span>
        </div>
        <div className="section-marker mb-2">Crear cuenta</div>
        <h2 className="text-3xl font-medium tracking-tight mb-8">Únete al sistema.</h2>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Nombre</label>
            <input required value={form.nombre} onChange={set("nombre")} className="w-full border border-zinc-300 px-4 py-3" data-testid="reg-nombre" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Email</label>
            <input required type="email" value={form.email} onChange={set("email")} className="w-full border border-zinc-300 px-4 py-3" data-testid="reg-email" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Contraseña</label>
            <input required type="password" minLength={6} value={form.password} onChange={set("password")} className="w-full border border-zinc-300 px-4 py-3" data-testid="reg-password" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Rol</label>
            <select value={form.role} onChange={set("role")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="reg-role">
              <option value="facultad">{config.facultad_label_singular || "Facultad"}</option>
              {config.enable_unidades && (
                <option value="unidad">{config.unidad_label_singular || "Unidad Académica"}</option>
              )}
              <option value="admin">Oficina de Vinculación</option>
              <option value="directivo">Directivo</option>
            </select>
          </div>
          {form.role === "facultad" && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{config.facultad_label_singular || "Facultad"}</label>
              <select required value={form.facultad_id} onChange={set("facultad_id")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="reg-facultad">
                <option value="">Selecciona...</option>
                {facultades.map((f) => (
                  <option key={f.id} value={f.id}>{f.nombre}</option>
                ))}
              </select>
            </div>
          )}
          {form.role === "unidad" && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{config.unidad_label_singular || "Unidad"}</label>
              <select required value={form.unidad_id} onChange={set("unidad_id")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="reg-unidad">
                <option value="">Selecciona...</option>
                {availableUnidades.map((u) => (
                  <option key={u.id} value={u.id}>{u.nombre}{u.facultad_nombre ? ` · ${u.facultad_nombre}` : ""}</option>
                ))}
              </select>
            </div>
          )}

          <button type="submit" disabled={loading} className="w-full bg-zinc-950 text-white px-6 py-4 font-medium hover:bg-zinc-800 disabled:opacity-50" data-testid="register-submit">
            {loading ? "Creando..." : "Crear cuenta"}
          </button>
        </form>

        <div className="mt-6 text-sm text-zinc-500">
          ¿Ya tienes cuenta? <Link to="/login" className="underline text-zinc-950">Ingresar</Link>
        </div>
      </div>
    </div>
  );
}
